// Central company configuration. All owner-confirmed values live in COMPANY.
// Remaining placeholders (team names on About) render visually flagged via <Token />.

export const COMPANY = {
  name: 'IDB HVAC Solutions',
  legalName: 'ID Builder HVAC Solutions LLC',
  alternateName: 'IDB HVAC',
  founderName: 'Sebastian Suciu',
  foundingDate: '2025-01-31', // matches the Google Business Profile opening date
  phoneMain: '425-405-5365',
  phoneMainHref: 'tel:+14254055365',
  email: 'Sebastian@idbhvac.com',
  license: 'IDBHVH*754BU',
  licenseLine: 'Licensed, Bonded & Insured — WA Lic. #IDBHVH*754BU',
  hours: 'Mon–Fri 7:00 AM – 5:00 PM',
  siteUrl: 'https://idbhvac.com',
  addressFederalWay: '36058 21st Ln S',
  yearsInBusiness: '2',
  financingProviders: 'Acorn Finance, HFS Financial and FTL Finance', // HFS brief + FTL (Nov 2026): the three partners — the list itself lives in src/config/financing.js
  brands: ['Mitsubishi Electric', 'Daikin'],
};

/** Public profiles — used for schema.org `sameAs` and the footer. Keep in step with the Google Business Profile. */
export const SOCIAL_LINKS = {
  googleMaps: 'https://www.google.com/maps?cid=16950663102576591112',
  googleReview: 'https://g.page/r/CQgBZohI2jzrEBM/review',
  linkedin: 'https://www.linkedin.com/company/idbhvac/',
  facebook: 'https://www.facebook.com/idbhvac',
  instagram: 'https://www.instagram.com/idbhvac',
  yelp: 'https://www.yelp.com/biz/idb-hvac-solutions-federal-way',
};

/**
 * The ID Builder family of companies (SEO Batch B, Part 2): the footer "Our companies" column and
 * the sentence on /about. Owner-supplied names, URLs and one-line descriptions — nothing else.
 */
export const COMPANY_FAMILY = [
  { key: 'hvac', name: 'IDB HVAC Solutions', href: '/', desc: 'Heating and cooling installation — this site', internal: true },
  { key: 'construction', name: 'ID Builder Construction', href: 'https://idbuilderconstruction.com', desc: 'General contracting, remodels and new construction' },
  { key: 'plumbing', name: 'IDB Plumbing Solutions', href: 'https://idb-plumbing.com', desc: 'Plumbing, drains and water heaters' },
];

/**
 * Blog author (SEO Batch B, Part 6.1 — owner's exact wording, 8 Oct 2026): the author box and the
 * Article `author` Person both read "Sebastian Suciu, co-owner, IDB HVAC Solutions". No other title.
 */
export const AUTHOR = {
  name: 'Sebastian Suciu',
  title: 'co-owner',
  org: COMPANY.name,
  line: `Sebastian Suciu, co-owner, ${COMPANY.name}`,
  path: '/about',
};

export const SOUTH_SOUND_CITIES = [
  'Federal Way', 'Tacoma', 'Kent', 'Auburn', 'Renton', 'Seattle', 'Bellevue', 'Puyallup',
  'Des Moines', 'Burien', 'SeaTac', 'Tukwila', 'Covington', 'Maple Valley', 'Lakewood', 'University Place',
];

export const NORTH_SOUND_CITIES = [
  'Snohomish', 'Everett', 'Lynnwood', 'Marysville', 'Monroe', 'Lake Stevens', 'Bothell',
  'Mill Creek', 'Mukilteo', 'Edmonds', 'Arlington', 'Mountlake Terrace', 'Woodinville', 'Granite Falls',
];

export const NAV_SERVICES = [
  { label: 'Residential Installation', to: '/residential-hvac-installation', desc: 'Heat pumps, furnaces, AC, and ductless for Puget Sound homes' },
  { label: 'Commercial Installation', to: '/commercial-hvac-installation', desc: 'RTUs, VRF, make-up air, and tenant improvements' },
  { label: 'Industrial / Mechanical', to: '/industrial-hvac-installation', desc: 'Large-tonnage systems and process ventilation' },
  { label: 'Emergency Service', to: '/emergency-hvac-service', desc: 'No heat or no cooling? Priority same-day response' },
  { label: 'Maintenance & Tune-Ups', to: '/hvac-maintenance-tune-up', desc: 'Seasonal tune-ups that keep systems efficient and under warranty' },
];
// The /services overview: first item in the header dropdown ("All services") and the footer
// services column ("All Services"), ahead of the five service pages above.
export const NAV_ALL_SERVICES = { to: '/services', desc: 'Everything we install, in one place' };

/**
 * Editable copy strings (brief 01 §1). `rebate_generic` is the only sentence the site uses to talk
 * about rebate amounts — never quote a dollar figure with a date in page copy; edit it here instead.
 * The backend promo-banner default (server.py BANNER_MESSAGE_CURRENT) carries the same sentence.
 */
export const COPY = {
  rebate_generic:
    'Utility rebates vary by provider and change during the year. We\u2019re a PSE Trade Ally and we check and file your rebate for you.',
  rebate_generic_lead: 'Utility rebates vary by provider and change during the year.',
};

/**
 * Owner review (8 Oct 2026): the generic rebate sentence names the visitor's ACTUAL electric utility.
 *  - PSE territory → the existing PSE Trade Ally sentence, word for word
 *  - another utility we could place from the ZIP (Snohomish County PUD, Seattle City Light, Tacoma Power…) → named
 *  - no utility / low confidence / a ZIP shared by two utilities → "your electric utility", no name
 * `utility` is the result's `utility` object ({found, electric_utility, electric_label, confidence, needs_choice}).
 */
export const rebateSentenceFor = (utility) => {
  const named = !!(utility && utility.found && utility.electric_label && !utility.needs_choice && utility.confidence !== 'low');
  if (named && utility.electric_utility === 'pse') return COPY.rebate_generic;
  if (named) return `${COPY.rebate_generic_lead} We check ${utility.electric_label}\u2019s current rebates for your home and file the paperwork for you.`;
  return `${COPY.rebate_generic_lead} We check your electric utility\u2019s current rebates for your home and file the paperwork for you.`;
};

/** Service-desk settings shown in the UI (display-only strings; owner confirms the numbers). */
export const SERVICE = {
  diagnostic_fee: '$99', // shown on the calculator's "system is down" screen; credited to the install if we replace
  diagnosticPath: '/emergency-hvac-service', // where "Book a diagnostic visit" sends people
};

/**
 * Calculator page feature blurbs (brief 02 'Copy edits') — one set per results arm. `icon` is a
 * lucide-react icon name resolved in pages/CalculatorPage.js.
 */
export const CALC_BLURBS = {
  open: [
    { icon: 'Eye', title: 'No form before your number', text: 'You see your price first. We only ask for an email if you want the PDF, or a phone if you want to book.' },
    { icon: 'ShieldCheck', title: 'A range, never a fake price', text: 'Honest planning numbers confirmed by a free on-site measure.' },
    { icon: 'Ruler', title: 'Confirmed on site, free', text: 'A free visit with a Manual J load calculation turns the range into a signed quote.' },
  ],
  gated: [
    { icon: 'Clock', title: 'About 2 minutes', text: 'One question per screen. Go back and change any answer.' },
    { icon: 'Lock', title: 'No early contact gate', text: 'We only ask who you are at the very end — to deliver your estimate.' },
    { icon: 'ShieldCheck', title: 'A range, never a fake price', text: 'Honest planning numbers confirmed by a free on-site measure.' },
  ],
};

/** Open-mode capture cards under the number (brief 02 'open mode flow'). */
export const CALC_CAPTURE = {
  save: {
    title: 'Save this estimate',
    micro: 'No phone number needed. We\u2019ll send a PDF with everything above.',
    button: 'Email me the PDF',
    sent: 'Sent to',
  },
  lock: {
    title: 'Lock in your number',
    body: 'Book a free on-site measure and we\u2019ll turn this range into a firm, signed quote.',
    button: 'Book my free measure',
  },
  call: 'Prefer to talk? Call',
  privacy: 'We only use this to deliver your estimate and follow up about it.',
};

/**
 * Trust strip items (brief 01 §2) — rendered in this order by <TrustBar /> (Shared.js) on the home
 * page and under the calculator. Reorder or edit here; `icon` is a lucide-react icon name resolved in
 * Shared.js (BadgeCheck, ShieldCheck, Award, Thermometer, Handshake, ClipboardCheck).
 */
export const TRUST_ITEMS = [
  { key: 'license', label: `WA Lic. #${COMPANY.license}`, icon: 'BadgeCheck', emphasis: true },
  { key: 'bonded', label: 'Licensed, Bonded & Insured', icon: 'ShieldCheck' },
  { key: 'mitsubishi', label: 'Mitsubishi factory-trained', icon: 'Award' },
  { key: 'epa', label: 'EPA 608 certified', icon: 'Thermometer' },
  { key: 'pse', label: 'PSE Trade Ally', icon: 'Handshake' },
  { key: 'permits', label: 'Permit pulled & closed on every install', icon: 'ClipboardCheck' },
];

// Branded imagery rendered with the real IDB logo (fleet wraps carry the
// actual mark, phone, website, and license line).
export const IMAGES = {
  heroHome: '/images/hero-home.webp',
  residential: '/images/residential.webp',
  commercial: '/images/commercial.webp',
  industrial: '/images/industrial.webp',
  emergency: '/images/emergency.webp',
  tuneup: '/images/tuneup.webp',
  fleet: '/images/fleet.webp',
  about: '/images/stock-spiral-duct.webp',
  federalWay: '/images/stock-seattle-skyline.webp',
  snohomish: '/images/stock-snohomish-estuary.webp',
  mechanicalRoom: '/images/stock-mechanical-room.webp',
  piping: '/images/stock-industrial-piping.webp',
  servicesHero: '/images/stock-dark-industrial.webp',
  financing: '/images/stock-financing.webp',
  darkIndustrial: '/images/stock-dark-industrial.webp',
};
